import { Alert, AlertDescription, AlertTitle } from "@papier/ui/components/ui/alert";
import { Button } from "@papier/ui/components/ui/button";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { CatchNotFound, useRouter } from "@tanstack/react-router";
import { DangerCircle as AlertCircleIcon, ArrowLeft } from "@papier/ui/components/icons";
import { useEffect } from "react";
import { ProjectNotFound, TemplateNotFound } from "./not-found";

interface ErrorProps {
  error: Error;
}

const isNotFoundError = (error: Error) =>
  "code" in error && (error as Error & { code?: unknown }).code === "NOT_FOUND";

function useErrorReset() {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  return router;
}

export function ErrorAlert({ error, onRetry }: { error: string; onRetry?: () => void }) {
  console.error("Error Alert:", error);
  const router = useErrorReset();
  return (
    <Alert variant="destructive">
      <AlertCircleIcon />
      <AlertTitle>{error}</AlertTitle>
      <AlertDescription>Something went wrong. Please try again.</AlertDescription>
      <div className="flex gap-3 pt-5">
        <Button
          type="button"
          variant="destructive"
          onClick={() => {
            onRetry?.();
            void router.invalidate();
          }}
        >
          Try Again
        </Button>
        <Button variant="ghost" className="text-foreground" onClick={() => router.history.back()}>
          <ArrowLeft />
          Back
        </Button>
      </div>
    </Alert>
  );
}

export function ProjectErrorComponent({ error }: ErrorProps) {
  if (isNotFoundError(error)) {
    return (
      <CatchNotFound>
        <ProjectNotFound message={error.message} />
      </CatchNotFound>
    );
  }
  return <ErrorAlert error={error.message} />;
}

export function TemplateErrorComponent({ error }: ErrorProps) {
  if (isNotFoundError(error)) {
    return (
      <CatchNotFound>
        <TemplateNotFound message={error.message} />
      </CatchNotFound>
    );
  }
  return <ErrorAlert error={error.message} />;
}

export function UsersErrorComponent({ error }: ErrorProps) {
  return <ErrorAlert error={error.message} />;
}
